/** Short relative time for the Portal activity feed. */
export function relativeTime(at: number): string {
  const minutes = Math.round((Date.now() - at) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  return `${Math.round(days / 7)} week${days < 14 ? '' : 's'} ago`
}

function parseISO(iso: string): Date | null {
  if (!iso) return null
  const parsed = new Date(`${iso}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** "Friday 25 September" */
export function formatShootDate(iso: string): string {
  const date = parseISO(iso)
  if (!date) return ''
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

export function shootDay(iso: string): string {
  const date = parseISO(iso)
  return date ? String(date.getDate()).padStart(2, '0') : '—'
}

export function shootMonth(iso: string): string {
  const date = parseISO(iso)
  return date ? date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase() : ''
}

export function shootYear(iso: string): string {
  const date = parseISO(iso)
  return date ? String(date.getFullYear()) : ''
}

/** A shoot stops being "next" once its date has passed. */
export function isUpcoming(iso: string): boolean {
  const date = parseISO(iso)
  if (!date) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date.getTime() >= today.getTime()
}

/**
 * Accepts "17", "00:17", "1:24", "1:02:03" and normalises to video timecode.
 * Returns '' when nothing usable was entered.
 */
export function normaliseTimecode(input: string): string {
  const value = (input ?? '').trim()
  if (!value) return ''

  const parts = value.split(':').map((part) => part.trim())
  if (parts.some((part) => part === '' || !/^\d+$/.test(part)) || parts.length > 3) return ''

  const numbers = parts.map(Number)
  let total = 0
  if (numbers.length === 1) total = numbers[0]
  else if (numbers.length === 2) total = numbers[0] * 60 + numbers[1]
  else total = numbers[0] * 3600 + numbers[1] * 60 + numbers[2]

  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const mm = String(minutes).padStart(2, '0')
  const ss = String(seconds).padStart(2, '0')
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`
}

/** "25 AUG · 5:16 PM" in the viewer's own timezone, for history rows. */
export function formatRevisionStamp(at: number): string {
  const date = new Date(at)
  const day = date
    .toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    .toUpperCase()
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${day} · ${time}`
}

/** "25 Aug, 3:47 PM" in the viewer's own timezone. */
export function formatEditStamp(at: number): string {
  const date = new Date(at)
  const day = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${day}, ${time}`
}

/** Greeting from the viewer's own device clock — no fixed timezone. */
export function greeting(now: Date = new Date()): string {
  const hour = now.getHours()
  if (hour >= 5 && hour < 12) return 'Good Morning'
  if (hour >= 12 && hour < 18) return 'Good Afternoon'
  return 'Good Evening'
}

export const MINUTE = 60000
export const HOUR = 3600000
export const DAY = 86400000
